import { FC } from "react";
import { MuiBox, MuiStepper, MuiTypography } from "../../../../components";
import { BULK_TYPE, SummaryStatus } from "../../../../helpers/constants";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { bulkActivitySiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateBulkActivityStepperWrapper } from "./CreateBulkActivityStepper.style";

interface CreateBulkActivityStepperProps {
  currentStep: number;
  isParticipantSelected: boolean;
  isActivityAdded: boolean;
  isAllProcessed?: boolean;
  status?: string;
}

export const CreateBulkActivityStepper: FC<CreateBulkActivityStepperProps> = ({
  currentStep,
  isParticipantSelected,
  isActivityAdded,
  isAllProcessed,
  status = "",
}) => {
  const { fetchLabel } = useSiteCore();

  const getStepperLabel = (key: string) => {
    return fetchLabel(bulkActivitySiteCoreKeys.bulkActivityStepperKey, key);
  };

  const getHeaderLabelValueForProcessingStep = () => {
    if (status === SummaryStatus.PROCESSING) {
      return getStepperLabel(bulkActivitySiteCoreKeys.processingProgress);
    } else {
      return getStepperLabel(bulkActivitySiteCoreKeys.almostThere);
    }
  };
  const getSubHeaderLabelValueForProcessingStep = () => {
    if (status === SummaryStatus.PROCESSING) {
      return getStepperLabel(
        bulkActivitySiteCoreKeys.processingYourRecordsMessage,
      );
    } else {
      return isActivityAdded
        ? getStepperLabel(
            bulkActivitySiteCoreKeys.addParticipantActivitiesMessage,
          )
        : getStepperLabel(bulkActivitySiteCoreKeys.addActivitiesMessage);
    }
  };
  const getStepperLabels = () => {
    let header = "";
    let subHeader = "";

    switch (currentStep) {
      case 0:
        header = getStepperLabel(bulkActivitySiteCoreKeys.letsStart);
        subHeader = isParticipantSelected
          ? getStepperLabel(
              bulkActivitySiteCoreKeys.chooseMoreParticipantMessage,
            )
          : getStepperLabel(bulkActivitySiteCoreKeys.chooseParticipantMessage);
        break;

      case 1:
        header = getHeaderLabelValueForProcessingStep();
        subHeader = getSubHeaderLabelValueForProcessingStep();
        break;

      case 2:
        header = isAllProcessed
          ? getStepperLabel(bulkActivitySiteCoreKeys.greatYouareDone)
          : getStepperLabel(bulkActivitySiteCoreKeys.youAreDone);
        subHeader = isAllProcessed
          ? getStepperLabel(
              bulkActivitySiteCoreKeys.addAdditionalBulkActivities,
            )
          : getStepperLabel(
              bulkActivitySiteCoreKeys.unprocessedParticipantMessage,
            );
        break;

      default:
        header = getStepperLabel(bulkActivitySiteCoreKeys.letsStart);
        subHeader = getStepperLabel(
          bulkActivitySiteCoreKeys.chooseParticipantMessage,
        );
        break;
    }

    return { header, subHeader };
  };

  return (
    <CreateBulkActivityStepperWrapper data-testid="create-bulk-activity-stepper">
      <MuiStepper
        activeStep={currentStep === 2 ? 3 : currentStep}
        status={status}
        steps={[
          {
            title: getStepperLabel(
              bulkActivitySiteCoreKeys.participantProgress,
            ),
          },
          {
            title:
              status === SummaryStatus.PROCESSING
                ? getStepperLabel(bulkActivitySiteCoreKeys.processingProgress)
                : getStepperLabel(
                    bulkActivitySiteCoreKeys.activityDetailsProgress,
                  ),
          },
          {
            title: getStepperLabel(bulkActivitySiteCoreKeys.successProgress),
          },
        ]}
        bulkType={BULK_TYPE.ACTIVITY}
      />

      <MuiBox className="stepper-content">
        <MuiTypography variant="h1Bold" className="stepper-title">
          {getStepperLabels().header}
        </MuiTypography>
        <MuiTypography
          variant="h2Bold"
          className="stepper-description"
          component="div"
        >
          {getStepperLabels().subHeader}
        </MuiTypography>
      </MuiBox>
    </CreateBulkActivityStepperWrapper>
  );
};
